<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { CMD_WOOW_LINK_CUSTOMER } from 'dashboard/helper/commandbar/events';
import { useWoowCrm, customerLabel } from 'dashboard/composables/useWoowCrm';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const conversationId = computed(() => props.conversationId);
const { summary, linkedCustomer, isUnavailable, fetchSummary } =
  useWoowCrm(conversationId);

// Paints from the cached attributes the resolver wrote, then revalidates —
// so switching conversations never shows a spinner where a name should be.
watch(
  conversationId,
  id => {
    if (id) fetchSummary();
  },
  { immediate: true }
);

const label = computed(() =>
  linkedCustomer.value
    ? customerLabel(linkedCustomer.value)
    : t('WOOW_CRM.CHIP.UNLINKED')
);

const openLinkDialog = () => emitter.emit(CMD_WOOW_LINK_CUSTOMER);
</script>

<template>
  <span>
    <!-- Not a button: there is nothing the agent can usefully click when the
      bridge itself is unreachable, and offering the link dialog here would only
      surface the same failure a second time. -->
    <span
      v-if="isUnavailable"
      v-tooltip="summary?.error || t('WOOW_CRM.CHIP.UNAVAILABLE_TOOLTIP')"
      class="flex items-center gap-1 px-2 py-1 rounded-md max-w-40 bg-n-alpha-2 text-n-slate-11"
    >
      <span class="flex-shrink-0 size-3 i-lucide-unplug" />
      <span class="text-xs font-medium truncate">
        {{ t('WOOW_CRM.CHIP.UNAVAILABLE') }}
      </span>
    </span>
    <button
      v-else-if="summary"
      v-tooltip="
        linkedCustomer
          ? t('WOOW_CRM.CHIP.LINKED_TOOLTIP', { ste: linkedCustomer.user_id })
          : t('WOOW_CRM.CHIP.UNLINKED_TOOLTIP')
      "
      type="button"
      class="flex items-center gap-1 px-2 py-1 rounded-md max-w-40"
      :class="
        linkedCustomer
          ? 'bg-n-alpha-2 text-n-slate-12'
          : 'bg-n-amber-3 text-n-amber-11'
      "
      @click="openLinkDialog"
    >
      <span
        class="flex-shrink-0 size-3"
        :class="linkedCustomer ? 'i-lucide-user-check' : 'i-lucide-user-x'"
      />
      <span class="text-xs font-medium truncate">{{ label }}</span>
    </button>
  </span>
</template>
